import { useTranslation } from 'react-i18next';
import { Stack, SxProps, Tooltip, Typography } from '@mui/material';
import { WORK_PAGE_NS } from '../../i18n';
import {
  IUser,
  IWork,
  renderDateTime,
  renderUTC,
  squashWorkUsers,
  StatusEnum,
  TStatus,
  UserAvatar,
} from '@diana/core';
import { useMemo } from 'react';

export function Status({
  user,
  status,
  isoDateTime,
  renderTime = renderDateTime,
  renderTimeTooltip = renderUTC,
  sx = {},
}: {
  user?: null | IUser;
  status?: TStatus;
  isoDateTime: string;
  renderTime?: (dateTime: string) => string;
  renderTimeTooltip?: (dateTime: string) => string;
  sx?: SxProps;
}) {
  const { t } = useTranslation(WORK_PAGE_NS);

  const tooltipTime = useMemo(
    () => renderTimeTooltip(isoDateTime),
    [renderTimeTooltip, isoDateTime],
  );

  const tooltip = useMemo(
    () => (renderTimeTooltip === renderUTC ? `UTC: ${tooltipTime}` : tooltipTime),
    [renderTimeTooltip, tooltipTime],
  );

  return (
    <Stack direction="row" justifyContent="flex-start" alignItems="center" sx={sx}>
      {user ? (
        <UserAvatar user={user} sx={{ fontSize: '12px', width: '24px', height: '24px', mr: 1 }} />
      ) : null}
      {status ? (
        <Typography variant="subtitle2" sx={{ mr: 1 }}>
          {t(`status.${status}`)}:
        </Typography>
      ) : null}

      <Tooltip title={tooltip} placement="top-end">
        <Typography variant="subtitle2">{renderTime(isoDateTime)}</Typography>
      </Tooltip>
    </Stack>
  );
}

export function WorkCreated({ work }: { work: null | IWork }) {
  const user = useMemo(() => {
    if (!work) return null;
    return squashWorkUsers(work.users).find((user) => user.id === work.createdBy?.id);
  }, [work]);

  if (!work) return null;
  return <Status status={StatusEnum.CREATED} isoDateTime={work.createdDate} user={user} />;
}
